import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const LA = 'America/Los_Angeles';
const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATE_FORMAT = 'YYYY-MM-DD';

/** Parse YYYY-MM-DD as midnight in LA (not UTC) and return a dayjs instance. */
function parseLADateKey(key) {
  return dayjs.tz(key, DATE_FORMAT, LA);
}

export function todayLAKey() {
  return dayjs().tz(LA).format('YYYY-MM-DD');
}

export function utcToLADateKey(date) {
  return dayjs(date).tz(LA).format('YYYY-MM-DD');
}

/** Safely convert a Date/ISO value to an LA date key (YYYY-MM-DD). Returns null if missing or unparseable. */
export function safeToLADateKey(value) {
  if (value == null || value === '') return null;
  try {
    const d = dayjs(value);
    if (!d.isValid()) return null;
    return d.tz(LA).format('YYYY-MM-DD');
  } catch {
    return null;
  }
}

/**
 * Derive LA date key from a Travel-form datetime (arrival/departure).
 * Form values are often stored as UTC midnight for a calendar day the user chose in LA;
 * interpreting that instant in LA can shift the date. Use the ISO date part (YYYY-MM-DD) as the LA day.
 */
export function travelDateTimeToLADateKey(value) {
  if (value == null || value === '') return null;
  try {
    const s = typeof value === 'string' ? value : value instanceof Date ? value.toISOString() : String(value);
    const datePart = s.slice(0, 10);
    if (DATE_KEY_RE.test(datePart) && dayjs(datePart, DATE_FORMAT).isValid()) return datePart;
    return safeToLADateKey(value);
  } catch {
    return null;
  }
}

export function laDateKeyToUTC(key) {
  return parseLADateKey(key).toDate();
}

export function laDateRangeToUTCBounds(fromKey, toKey) {
  return {
    start: parseLADateKey(fromKey).toDate(),
    end: parseLADateKey(toKey).endOf('day').toDate(),
  };
}

export function validateLADateKey(value) {
  if (typeof value !== 'string' || !DATE_KEY_RE.test(value)) {
    return { valid: false, message: 'date must be YYYY-MM-DD (LA calendar day)' };
  }
  if (value.includes('T') || value.includes(' ')) {
    return { valid: false, message: 'date must not contain a time portion' };
  }
  const parsed = parseLADateKey(value);
  if (!parsed.isValid()) {
    return { valid: false, message: `Invalid date for ${LA}` };
  }
  if (parsed.format('YYYY-MM-DD') !== value) {
    return { valid: false, message: `Invalid date for ${LA}` };
  }
  return { valid: true };
}

/** One LA day for UI: weekday + short month (e.g. Wed, 24 Mar 2026). */
export function formatLADateKeyReadable(key) {
  if (typeof key !== 'string' || !DATE_KEY_RE.test(key)) return key == null ? '' : String(key);
  const parsed = parseLADateKey(key);
  return parsed.isValid() ? parsed.format('ddd, D MMM YYYY') : key;
}

/** Same day or "Wed, 24 Mar 2026 → Fri, 26 Mar 2026" (arrow, no hyphen clutter). */
export function formatLADateRangeArrow(fromKey, toKey) {
  if (fromKey === toKey) return formatLADateKeyReadable(fromKey);
  return `${formatLADateKeyReadable(fromKey)} → ${formatLADateKeyReadable(toKey)}`;
}

/** @deprecated Prefer formatLADateKeyReadable; kept for imports. */
export function formatLADateKeyLong(key) {
  return formatLADateKeyReadable(key);
}

/**
 * Turn stored "YYYY-MM-DD" or "YYYY-MM-DD to YYYY-MM-DD" into readable text.
 * Non-ISO strings are returned unchanged (e.g. already formatted).
 */
export function addDaysToLADateKey(key, days = 1) {
  return parseLADateKey(key).add(days, 'day').format('YYYY-MM-DD');
}

/** LA date keys in [fromKey, toKey) — checkout-exclusive. */
export function buildDateKeys(fromKey, toKey) {
  const keys = [];
  let k = fromKey;
  while (k && toKey && k < toKey) {
    keys.push(k);
    k = addDaysToLADateKey(k, 1);
  }
  return keys;
}

export function formatLADateKeyOrRangeForDisplay(value) {
  if (value == null || value === '') return '';
  if (typeof value !== 'string') return String(value);
  const v = value.trim();
  if (v.includes(' → ')) return v;
  if (v.includes(' to ')) {
    const parts = v.split(/\s+to\s+/).map((p) => p.trim());
    if (parts.length === 2 && DATE_KEY_RE.test(parts[0]) && DATE_KEY_RE.test(parts[1])) {
      return formatLADateRangeArrow(parts[0], parts[1]);
    }
    return parts.map((p) => (DATE_KEY_RE.test(p) ? formatLADateKeyReadable(p) : p)).join(' → ');
  }
  if (DATE_KEY_RE.test(v)) return formatLADateKeyReadable(v);
  return v;
}
